import { createStore } from "zustand";

import {
  computeExtendFillRange,
  computeExtendSelection,
  computeMoveSelection,
  computeSetAnchor,
  computeStartFillDrag,
  computeTabAdvance,
  computeTabReverse,
  normalizedRange
} from "./cell_selection_utils";
import {
  ArrowDirection,
  CellCoord,
  CellRange,
  DragAxis,
  EditTrigger,
  FillEvent,
  TableBounds
} from "./interactive_table.types";

export type InteractiveTableState = {
  selectionRange: CellRange | null;
  fillRange: CellRange | null;
  copiedRange: CellRange | null;
  isDragging: boolean;
  isFillDragging: boolean;
  editModeCoord: CellCoord | null;
  editModeTrigger: EditTrigger | null;
  nonInteractiveLastColumn: boolean;
};

export type InteractiveTableActions = {
  setAnchor: (coord: CellCoord) => void;
  startDrag: () => void;
  startFillDrag: (anchor: CellCoord) => void;
  extendSelection: (target: CellCoord, axis: DragAxis) => void;
  extendFillRange: (target: CellCoord, axis: DragAxis) => void;
  endDrag: (onFill?: (event: FillEvent) => void) => void;
  enterEditMode: (coord: CellCoord, trigger: EditTrigger) => void;
  exitEditMode: () => void;
  clearSelection: () => void;
  moveSelection: (direction: ArrowDirection, shiftHeld: boolean, axis: DragAxis, bounds: TableBounds) => void;
  tabAdvance: (bounds: TableBounds) => void;
  tabReverse: (bounds: TableBounds) => void;
  setCopied: (range: CellRange) => void;
  clearCopied: () => void;
  setSelection: (range: CellRange) => void;
  setNonInteractiveLastColumn: (value: boolean) => void;
};

export type InteractiveTableStoreType = InteractiveTableState & {
  actions: InteractiveTableActions;
};

export type InteractiveTableStoreApi = ReturnType<typeof createInteractiveTableStore>;

export const createInteractiveTableStore = () =>
  createStore<InteractiveTableStoreType>()((set) => ({
    selectionRange: null,
    fillRange: null,
    copiedRange: null,
    isDragging: false,
    isFillDragging: false,
    editModeCoord: null,
    editModeTrigger: null,
    nonInteractiveLastColumn: false,

    actions: {
      setAnchor: (coord) => set({ ...computeSetAnchor(coord), copiedRange: null }),
      startDrag: () =>
        set((state) => {
          if (!state.selectionRange) {
            return state;
          }

          return { isDragging: true };
        }),
      startFillDrag: (anchor) => set(computeStartFillDrag(anchor)),
      extendSelection: (target, axis) =>
        set((state) => {
          if (!state.isDragging || !state.selectionRange) {
            return state;
          }

          return { selectionRange: computeExtendSelection(state.selectionRange, target, axis) };
        }),
      extendFillRange: (target, axis) =>
        set((state) => {
          if (!state.isFillDragging || !state.selectionRange) {
            return state;
          }

          return { fillRange: computeExtendFillRange(state.selectionRange, target, axis) };
        }),
      endDrag: (onFill) =>
        set((state) => {
          if (state.isFillDragging && state.fillRange && state.selectionRange) {
            const { anchor } = state.selectionRange;
            const filled = normalizedRange(state.fillRange);

            onFill?.({
              sourceCell: { ...anchor, value: undefined },
              targetRange: filled
            });

            // Promote the fill range into the selection so the user sees the
            // filled area highlighted after releasing the mouse.
            const promotedRange: CellRange = {
              anchor: { rowIndex: filled.minRow, colIndex: filled.minCol },
              active: { rowIndex: filled.maxRow, colIndex: filled.maxCol }
            };

            return {
              isDragging: false,
              isFillDragging: false,
              fillRange: null,
              selectionRange: promotedRange
            };
          }

          return { isDragging: false, isFillDragging: false, fillRange: null };
        }),
      enterEditMode: (coord, trigger) =>
        set({
          editModeCoord: coord,
          editModeTrigger: trigger,
          isDragging: false,
          isFillDragging: false,
          copiedRange: null
        }),
      exitEditMode: () => set({ editModeCoord: null, editModeTrigger: null }),
      clearSelection: () =>
        set({
          selectionRange: null,
          fillRange: null,
          copiedRange: null,
          editModeCoord: null,
          editModeTrigger: null
        }),
      setCopied: (range) => set({ copiedRange: range }),
      clearCopied: () => set({ copiedRange: null }),
      setSelection: (range) => set({ selectionRange: range, fillRange: null, editModeCoord: null }),
      setNonInteractiveLastColumn: (value) => set({ nonInteractiveLastColumn: value }),
      moveSelection: (direction, shiftHeld, axis, bounds) =>
        set((state) => {
          if (!state.selectionRange) {
            return state;
          }

          return {
            selectionRange: computeMoveSelection(state.selectionRange, direction, shiftHeld, axis, bounds),
            fillRange: null,
            editModeCoord: null,
            copiedRange: null
          };
        }),
      tabAdvance: (bounds) =>
        set((state) => {
          if (!state.selectionRange) {
            return state;
          }

          const next = computeTabAdvance(state.selectionRange.anchor, bounds);
          return {
            selectionRange: { anchor: next, active: next },
            fillRange: null,
            editModeCoord: null,
            editModeTrigger: null,
            copiedRange: null
          };
        }),
      tabReverse: (bounds) =>
        set((state) => {
          if (!state.selectionRange) {
            return state;
          }

          const next = computeTabReverse(state.selectionRange.anchor, bounds);
          return {
            selectionRange: { anchor: next, active: next },
            fillRange: null,
            editModeCoord: null,
            editModeTrigger: null,
            copiedRange: null
          };
        })
    }
  }));
